import type { ReactNode } from 'react';
import { Tabs } from '@paige/ui';
import { CopyButton } from '../company/CopyButton';
import { chooseLanguage, languages, useLanguage, type Language } from './language';
import '../company/intake.css';

export type Snippets = Partial<Record<Language, string>>;

/** One block of code in the mono face, with its copy button. */
export function Code({ code, label }: { code: string; label: string }): ReactNode {
  return (
    <div className="dev-code">
      <CopyButton value={code} label={`Copy ${label}`} />
      <pre className="dev-code__body" tabIndex={0}>
        <code>{code.trimEnd()}</code>
      </pre>
    </div>
  );
}

/**
 * The same thing in each language the page is written in, as tabs; choosing one chooses it for
 * every sample on the page. A language this sample has no version in is left out of its tabs.
 */
export function CodeSample({ code, label }: { code: Snippets; label: string }): ReactNode {
  const chosen = useLanguage();
  const items = languages
    .filter((l) => code[l.value] !== undefined)
    .map((l) => ({
      value: l.value,
      label: l.label,
      content: <Code code={code[l.value]!} label={`${label} in ${l.label}`} />,
    }));
  const value = items.some((i) => i.value === chosen) ? chosen : (items[0]?.value ?? chosen);
  return (
    <div className="dev-sample">
      <Tabs items={items} value={value} onValueChange={chooseLanguage} label={label} />
    </div>
  );
}
